/**
 * Cloudflare Turnstile widget — privacy-friendly captcha alternative.
 *
 * Why Turnstile (not reCAPTCHA):
 *   - No Google tracking; no third-party cookies.
 *   - First-party Cloudflare integration (we already use Pages + Workers).
 *   - Same-origin verification on the API side via the SECRET key.
 *
 * Wiring:
 *   1. Issue a site key from Cloudflare → Turnstile dashboard.
 *   2. Set `VITE_TURNSTILE_SITE_KEY` in `.env.production` (PUBLIC).
 *   3. Set `TURNSTILE_SECRET_KEY` as a wrangler secret on the API.
 *   4. The form submits the token; the API calls
 *      `https://challenges.cloudflare.com/turnstile/v0/siteverify`
 *      with the token + secret; rejects on failure (project-context
 *      §7.6.4).
 *
 * If `VITE_TURNSTILE_SITE_KEY` is unset, the widget renders a no-op
 * placeholder so dev builds don't break — the API should treat a
 * missing token as failure ONLY in production.
 */

import { useEffect, useRef, useState } from 'react'
import { useLocale } from '@/shared/i18n/use-translation'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__onTurnstileLoad'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          callback?: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
          size?: 'normal' | 'compact' | 'flexible'
          language?: string
          action?: string
        },
      ) => string
      remove: (widgetId: string) => void
      reset: (widgetId?: string) => void
    }
    __onTurnstileLoad?: () => void
  }
}

interface TurnstileProps {
  /** Action label — surfaces in Cloudflare analytics. */
  action?: string
  /** Called with the verification token; pass it to your API. */
  onVerify: (token: string) => void
  /** Called when the widget errors or the user fails the challenge. */
  onError?: () => void
  /** Called when an existing token expires (>~5 min). */
  onExpire?: () => void
  className?: string
}

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()

  scriptPromise = new Promise((resolve) => {
    window.__onTurnstileLoad = () => resolve()
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * Renders a Turnstile widget. Pure side-effect — no children. The
 * widget self-resizes; we wrap it in a fixed-min-height div so the
 * form layout doesn't jump when it loads.
 */
export function Turnstile({
  action,
  onVerify,
  onError,
  onExpire,
  className,
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const { locale } = useLocale()

  useEffect(() => {
    if (!SITE_KEY) {
      setUnavailable(true)
      return
    }
    let cancelled = false

    loadScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: onVerify,
        'error-callback': onError,
        'expired-callback': onExpire,
        theme: 'auto',
        size: 'flexible',
        language: locale === 'ar' ? 'ar' : 'en',
        action,
      })
    })

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          /* widget already removed */
        }
        widgetIdRef.current = null
      }
    }
    // intentionally only re-render on `action` / `locale` change so the
    // verify callback identity isn't a redraw trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, locale])

  if (unavailable) {
    // Dev / preview fallback. The API should treat a missing token as
    // a failure in production — not here.
    return (
      <div
        className={className}
        aria-hidden
        data-turnstile="unavailable"
        style={{ display: 'none' }}
      />
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ minHeight: 65 }}
      data-turnstile="container"
    />
  )
}

/** True only in production builds with a configured site key. */
export const TURNSTILE_ENABLED = Boolean(SITE_KEY)
